import React from 'react';
import { Container, Typography, Box, List, ListItem } from '@mui/material';
import PageHead from '../components/PageHead';

const TermsOfService = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHead 
        title="Terms of Service" 
        description="AutoBlogPilot's terms of service and usage agreement"
      />
      <Typography variant="h3" component="h1" gutterBottom>
        Terms of Service
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Effective Date: December 1, 2024
      </Typography>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          1. Introduction
        </Typography>
        <Typography paragraph>
          Welcome to AutoBlogPilot ("we," "us," or "our"). These Terms of Service ("Terms") govern your access to and use of our automated blogging and SEO platform available at autoblogpilot.com (the "Service"). By accessing or using our Service, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not use our Service.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          2. Eligibility
        </Typography>
        <Typography paragraph>
          By using the Service, you represent and warrant that:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>You are at least 18 years old or the age of majority in your jurisdiction.</ListItem>
            <ListItem>You have the legal capacity to enter into a binding contract.</ListItem>
            <ListItem>You are not prohibited from using the Service under any applicable laws.</ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          3. Account Registration
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          3.1 Account Creation
        </Typography>
        <Typography paragraph>
          To access certain features of the Service, you must register for an account. You agree to:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>Provide accurate, current, and complete information during the registration process.</ListItem>
            <ListItem>Maintain and promptly update your account information.</ListItem>
            <ListItem>Keep your password confidential and secure.</ListItem>
          </List>
        </Box>

        <Typography variant="h6" gutterBottom>
          3.2 Account Responsibility
        </Typography>
        <Typography paragraph>
          You are responsible for all activities that occur under your account. You agree to notify us immediately of any unauthorized use or security breach.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          4. Use of the Service
        </Typography>

        <Typography variant="h6" gutterBottom>
          4.1 License Grant
        </Typography>
        <Typography paragraph>
          We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your personal or internal business purposes in accordance with these Terms.
        </Typography>

        <Typography variant="h6" gutterBottom>
          4.2 User Conduct
        </Typography>
        <Typography paragraph>
          You agree not to:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>Use the Service for any unlawful or prohibited activities.</ListItem>
            <ListItem>Upload or transmit any harmful code or content.</ListItem>
            <ListItem>Interfere with or disrupt the integrity or performance of the Service.</ListItem>
            <ListItem>Attempt to gain unauthorized access to any part of the Service.</ListItem>
          </List>
        </Box>

        <Typography variant="h6" gutterBottom>
          4.3 Content Generation
        </Typography>
        <Typography paragraph>
          The Service generates content using artificial intelligence technologies provided by third-party providers like OpenAI and Anthropic. You acknowledge and agree that:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>Generated content is for informational purposes only.</ListItem>
            <ListItem>You are responsible for reviewing and ensuring the accuracy and compliance of the content.</ListItem>
            <ListItem>We do not guarantee that the generated content will be error-free or suitable for any specific purpose.</ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          5. Fees and Payment
        </Typography>

        <Typography variant="h6" gutterBottom>
          5.1 Subscription Plans
        </Typography>
        <Typography paragraph>
          Access to certain features of the Service may require payment of fees. You agree to pay all fees in accordance with the selected subscription plan.
        </Typography>

        <Typography variant="h6" gutterBottom>
          5.2 Billing
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>Fees are billed in advance on a recurring basis (e.g., monthly or annually).</ListItem>
            <ListItem>All payments are non-refundable except as expressly provided in these Terms.</ListItem>
          </List>
        </Box>

        <Typography variant="h6" gutterBottom>
          5.3 Taxes
        </Typography>
        <Typography paragraph>
          All fees are exclusive of taxes, levies, or duties imposed by taxing authorities. You are responsible for paying any such taxes.
        </Typography>

        {/* Continue with sections 6-15... */}
        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          6. Intellectual Property Rights
        </Typography>

        <Typography variant="h6" gutterBottom>
          6.1 Ownership
        </Typography>
        <Typography paragraph>
          All intellectual property rights in the Service, including but not limited to software, logos, trademarks, and content (excluding User Content), are owned by AutoBlogPilot or its licensors.
        </Typography>

        <Typography variant="h6" gutterBottom>
          6.2 User Content
        </Typography>
        <Typography paragraph>
          By submitting or uploading content through the Service ("User Content"), you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content solely for the purpose of providing the Service.
        </Typography>

        <Typography variant="h6" gutterBottom>
          6.3 Feedback
        </Typography>
        <Typography paragraph>
          Any feedback, suggestions, or ideas provided by you regarding the Service ("Feedback") may be used by us without any obligation to you.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          7. Third-Party Services
        </Typography>

        <Typography variant="h6" gutterBottom>
          7.1 Integrations
        </Typography>
        <Typography paragraph>
          The Service may integrate with third-party services (e.g., WordPress, social media platforms). Your use of such services is subject to their respective terms and policies.
        </Typography>

        <Typography variant="h6" gutterBottom>
          7.2 No Endorsement
        </Typography>
        <Typography paragraph>
          We do not endorse or assume responsibility for any third-party services, content, or products.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          8. Confidentiality
        </Typography>

        <Typography variant="h6" gutterBottom>
          8.1 Definition
        </Typography>
        <Typography paragraph>
          "Confidential Information" means any non-public information disclosed by us to you, either directly or indirectly, in writing, orally, or by inspection.
        </Typography>

        <Typography variant="h6" gutterBottom>
          8.2 Obligations
        </Typography>
        <Typography paragraph>
          You agree to:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>Use Confidential Information solely for the purpose of using the Service.</ListItem>
            <ListItem>Not disclose Confidential Information to any third party without our prior written consent.</ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          9. Disclaimers and Limitation of Liability
        </Typography>

        <Typography variant="h6" gutterBottom>
          9.1 Disclaimers
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>The Service is provided on an "AS IS" and "AS AVAILABLE" basis.</ListItem>
            <ListItem>We make no warranties or representations regarding the accuracy, reliability, or completeness of the Service.</ListItem>
          </List>
        </Box>

        <Typography variant="h6" gutterBottom>
          9.2 Limitation of Liability
        </Typography>
        <Typography paragraph>
          To the maximum extent permitted by law:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</ListItem>
            <ListItem>Our total liability arising out of or relating to these Terms shall not exceed the fees paid by you to us in the twelve (12) months preceding the claim.</ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          10. Indemnification
        </Typography>
        <Typography paragraph>
          You agree to indemnify, defend, and hold harmless AutoBlogPilot and its affiliates from any claims, liabilities, damages, losses, or expenses arising out of or relating to:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>Your use of the Service.</ListItem>
            <ListItem>Your violation of these Terms.</ListItem>
            <ListItem>Your violation of any third-party rights, including intellectual property rights.</ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          11. Term and Termination
        </Typography>

        <Typography variant="h6" gutterBottom>
          11.1 Term
        </Typography>
        <Typography paragraph>
          These Terms are effective upon your acceptance and continue until terminated.
        </Typography>

        <Typography variant="h6" gutterBottom>
          11.2 Termination by You
        </Typography>
        <Typography paragraph>
          You may terminate your account at any time by following the account deletion process on the Service.
        </Typography>

        <Typography variant="h6" gutterBottom>
          11.3 Termination by Us
        </Typography>
        <Typography paragraph>
          We may suspend or terminate your access to the Service if:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>You violate any provision of these Terms.</ListItem>
            <ListItem>We are required to do so by law.</ListItem>
          </List>
        </Box>

        <Typography variant="h6" gutterBottom>
          11.4 Effect of Termination
        </Typography>
        <Typography paragraph>
          Upon termination:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>Your right to access and use the Service will immediately cease.</ListItem>
            <ListItem>All provisions of these Terms that should survive termination will do so.</ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          12. Modifications to the Service and Terms
        </Typography>

        <Typography variant="h6" gutterBottom>
          12.1 Service Changes
        </Typography>
        <Typography paragraph>
          We reserve the right to modify or discontinue the Service or any part thereof at any time, with or without notice.
        </Typography>

        <Typography variant="h6" gutterBottom>
          12.2 Terms Changes
        </Typography>
        <Typography paragraph>
          We may update these Terms from time to time. If we make material changes, we will notify you by email or through the Service. Your continued use of the Service after the changes become effective constitutes your acceptance of the new Terms.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          13. Governing Law and Dispute Resolution
        </Typography>

        <Typography variant="h6" gutterBottom>
          13.1 Governing Law
        </Typography>
        <Typography paragraph>
          These Terms shall be governed by and construed in accordance with the laws of [Your Country/State], without regard to its conflict of law principles.
        </Typography>

        <Typography variant="h6" gutterBottom>
          13.2 Dispute Resolution
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem><strong>Arbitration:</strong> Any disputes arising out of or relating to these Terms shall be resolved through binding arbitration administered by [Arbitration Association] under its rules.</ListItem>
            <ListItem><strong>Venue:</strong> The arbitration shall take place in [Your City, Country/State].</ListItem>
            <ListItem><strong>Exception:</strong> Either party may seek injunctive relief in a court of competent jurisdiction.</ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          14. Miscellaneous
        </Typography>

        <Typography variant="h6" gutterBottom>
          14.1 Entire Agreement
        </Typography>
        <Typography paragraph>
          These Terms constitute the entire agreement between you and AutoBlogPilot regarding the Service.
        </Typography>

        <Typography variant="h6" gutterBottom>
          14.2 Severability
        </Typography>
        <Typography paragraph>
          If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
        </Typography>

        <Typography variant="h6" gutterBottom>
          14.3 Waiver
        </Typography>
        <Typography paragraph>
          No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.
        </Typography>

        <Typography variant="h6" gutterBottom>
          14.4 Assignment
        </Typography>
        <Typography paragraph>
          You may not assign or transfer your rights or obligations under these Terms without our prior written consent. We may assign our rights and obligations without restriction.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          15. Contact Information
        </Typography>
        <Typography paragraph>
          If you have any questions about these Terms, please contact us at:
        </Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          <Typography paragraph>
            Email: contact@autoblogpilot.com
          </Typography>
        </Typography>
      </Box>
    </Container>
  );
};

export default TermsOfService; 